import { makeAutoObservable } from 'mobx';

import {
  EvaluationName,
  IssueDetectedVariant,
  PrequalResult,
  ShortFormLeadResponse,
} from '@types';
import { MapiResponse as ShortFormLead } from '@types';
import { sendEventToHost } from '@utils';

import { store } from './store';
import { GTMService } from '../services/GTM';

export default class CommonStore {
  loading = false;
  enrollmentType: EvaluationName = '';
  gtmService = GTMService.getInstance();
  showOffRamp = false;
  shortFormLead: ShortFormLead = {};
  openDrawer = '';
  openProviders = false;
  showServicer = false;
  shortFormLoading = false;
  buttonLoading = false;
  mapiErrorVariant: IssueDetectedVariant | null = null;
  hasSentConversionToHost = false;
  resultsCtaAction: (() => void | Promise<void>) | null = null;
  selectedResultsProgram: PrequalResult | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  get appLoading(): boolean {
    return (
      store.userStore.userLoading ||
      store.partnerStore.partnerLoading ||
      store.initializeStore.isLoading ||
      store.featureFlagStore.isLoading
    );
  }

  setShowOffRamp = (showOffRamp: boolean) => {
    this.showOffRamp = showOffRamp;
  };

  setLoading = (isLoading: boolean) => {
    this.loading = isLoading;
  };

  setOpenDrawer(id: string) {
    this.openDrawer = id;
  }

  setOpenProviders(open: boolean) {
    this.openProviders = open;
  }

  setShowServicer = (showServicer: boolean) => {
    this.showServicer = showServicer;
  };

  setShortFormLead = (shortFormLead: ShortFormLeadResponse) => {
    this.shortFormLead = shortFormLead;
  };

  sendConversionToHost = (leadType?: 'DNQ') => {
    if (this.hasSentConversionToHost) return;

    if (leadType === 'DNQ') {
      sendEventToHost('conversion', {
        data: {
          leadType: 'DNQ',
        },
      });
      this.hasSentConversionToHost = true;
      return;
    }

    if (!this.shortFormLead.LeadId) return;

    sendEventToHost('conversion', {
      data: {
        leadid: this.shortFormLead.LeadId,
        leadguid: this.shortFormLead.LeadGUID,
        language: store.initializeStore.options.language ?? 'en',
        debtamount: store.formStore.formData['debtAmount'],
        firstName: store.formStore.formData['firstName'],
        lastName: store.formStore.formData['lastName'],
        email: store.formStore.formData['email'],
        phone: store.formStore.formData['phone'],
        zipCode: store.formStore.formData['zipCode'],
      },
    });
    this.hasSentConversionToHost = true;
  };

  setEnrollmentType = (type: EvaluationName) => {
    this.enrollmentType = type;
  };

  setButtonLoading = (isLoading: boolean) => {
    this.buttonLoading = isLoading;
  };

  /**
   * The results screen's primary CTA ("Personalize to Review Plan Details" /
   * "See My Plan Options"), registered by whichever results footer is mounted —
   * ProgramSelection's or WhiteLabel's. The win-back reuses it so its primary button performs
   * exactly the same action instead of duplicating the opt-in logic.
   */
  setResultsCtaAction = (action: (() => void | Promise<void>) | null) => {
    this.resultsCtaAction = action;
  };

  /**
   * The program card the user currently has selected on the results screen.
   * The results-page win-back mirrors this selection (and only shows the
   * "Good Fit" badge when it happens to be the recommended program).
   */
  setSelectedResultsProgram = (program: PrequalResult | null) => {
    this.selectedResultsProgram = program;
  };

  get hasMapiError(): boolean {
    return this.mapiErrorVariant !== null;
  }

  setMapiErrorVariant = (variant: IssueDetectedVariant | null) => {
    this.mapiErrorVariant = variant;
  };
}
